import { c as _c } from "react/compiler-runtime";
import { cn } from '@achra/ui/lib/utils';
import { getPercentDisplay, getShortCode, threeDigitsPrecisionHumanization } from '@/modules/finances/lib/doughnut-chart-utils';
import type { DoughnutSeries } from '@/modules/finances/types';
interface ItemLegendDoughnutProps {
  isDeepLevel?: boolean;
  changeAlignment: boolean;
  doughnutData: DoughnutSeries;
  toggleSeriesVisibility: (seriesName: string) => void;
  onLegendItemLeave: (legendName: string) => void;
  onLegendItemHover: (legendName: string) => void;
}
function ItemLegendDoughnut(t0) {
  const $ = _c(56);
  const {
    changeAlignment,
    isDeepLevel: t1,
    doughnutData,
    onLegendItemHover,
    onLegendItemLeave,
    toggleSeriesVisibility
  } = t0;
  const isDeepLevel = t1 === undefined ? true : t1;
  let t2;
  if ($[0] !== doughnutData.value) {
    t2 = threeDigitsPrecisionHumanization(doughnutData.value, true);
    $[0] = doughnutData.value;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const valueRounded = t2;
  let t3;
  if ($[2] !== doughnutData.percent) {
    t3 = getPercentDisplay(doughnutData.percent);
    $[2] = doughnutData.percent;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const percentDisplay = t3;
  const t4 = doughnutData.name;
  let t5;
  if ($[4] !== doughnutData.name || $[5] !== toggleSeriesVisibility) {
    t5 = () => {
      toggleSeriesVisibility(doughnutData.name);
    };
    $[4] = doughnutData.name;
    $[5] = toggleSeriesVisibility;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  let t6;
  if ($[7] !== doughnutData.name || $[8] !== onLegendItemHover) {
    t6 = () => {
      onLegendItemHover(doughnutData.name);
    };
    $[7] = doughnutData.name;
    $[8] = onLegendItemHover;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  let t7;
  if ($[10] !== doughnutData.name || $[11] !== onLegendItemLeave) {
    t7 = () => {
      onLegendItemLeave(doughnutData.name);
    };
    $[10] = doughnutData.name;
    $[11] = onLegendItemLeave;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  const t8 = isDeepLevel ? "gap-0.25" : "gap-none";
  const t9 = isDeepLevel ? "flex-row" : "flex-col";
  const t10 = changeAlignment ? "min-w-0" : "min-w-47.5";
  let t11;
  if ($[13] !== t10 || $[14] !== t8 || $[15] !== t9) {
    t11 = cn("group flex cursor-pointer gap-x-4 text-xs transition-colors duration-200", t8, t9, t10);
    $[13] = t10;
    $[14] = t8;
    $[15] = t9;
    $[16] = t11;
  } else {
    t11 = $[16];
  }
  const t12 = doughnutData.color || "blue";
  let t13;
  if ($[17] !== t12) {
    t13 = <div className="h-2 w-2 min-w-2 shrink-0 rounded-full" style={{
      backgroundColor: t12
    }} />;
    $[17] = t12;
    $[18] = t13;
  } else {
    t13 = $[18];
  }
  const t14 = isDeepLevel ? "w-fit" : "w-42.5";
  const t15 = isDeepLevel ? "xl:text-xs xl:leading-3.75" : "xl:text-sm/6";
  let t16;
  if ($[19] !== t14 || $[20] !== t15) {
    t16 = cn("leading-3.75 font-semibold", "overflow-hidden text-ellipsis whitespace-nowrap", t14, t15);
    $[19] = t14;
    $[20] = t15;
    $[21] = t16;
  } else {
    t16 = $[21];
  }
  let t17;
  if ($[22] !== doughnutData.code || $[23] !== doughnutData.name || $[24] !== isDeepLevel) {
    t17 = isDeepLevel ? getShortCode(doughnutData.code) : doughnutData.name;
    $[22] = doughnutData.code;
    $[23] = doughnutData.name;
    $[24] = isDeepLevel;
    $[25] = t17;
  } else {
    t17 = $[25];
  }
  let t18;
  if ($[26] !== t16 || $[27] !== t17) {
    t18 = <div className={t16}>{t17}</div>;
    $[26] = t16;
    $[27] = t17;
    $[28] = t18;
  } else {
    t18 = $[28];
  }
  let t19;
  if ($[29] !== t13 || $[30] !== t18) {
    t19 = <div className="flex flex-row items-center gap-1.5">{t13}{t18}</div>;
    $[29] = t13;
    $[30] = t18;
    $[31] = t19;
  } else {
    t19 = $[31];
  }
  const t20 = isDeepLevel ? "ml-1" : "ml-3.5";
  const t21 = isDeepLevel ? "leading-6" : "leading-normal";
  const t22 = isDeepLevel && "overflow-visible text-clip whitespace-normal";
  const t23 = isDeepLevel ? "xl:text-xs/3.75" : "xl:text-sm/normal";
  let t24;
  if ($[32] !== t20 || $[33] !== t21 || $[34] !== t22 || $[35] !== t23) {
    t24 = cn("flex text-xs font-medium", t20, t21, t22, t23);
    $[32] = t20;
    $[33] = t21;
    $[34] = t22;
    $[35] = t23;
    $[36] = t24;
  } else {
    t24 = $[36];
  }
  let t25;
  if ($[37] === Symbol.for("react.memo_cache_sentinel")) {
    t25 = cn("transition-colors", "text-foreground/50", "group-hover:text-foreground");
    $[37] = t25;
  } else {
    t25 = $[37];
  }
  const t26 = `(${percentDisplay}%)`;
  let t27;
  if ($[38] !== t26) {
    t27 = <span className={t25}>{t26}</span>;
    $[38] = t26;
    $[39] = t27;
  } else {
    t27 = $[39];
  }
  let t28;
  if ($[40] === Symbol.for("react.memo_cache_sentinel")) {
    t28 = cn("transition-colors", "text-foreground/50", "group-hover:text-foreground");
    $[40] = t28;
  } else {
    t28 = $[40];
  }
  let t29;
  if ($[41] !== valueRounded.suffix || $[42] !== valueRounded.value) {
    t29 = <div className="ml-1 flex"><span className={t28}>{valueRounded.value} {valueRounded.suffix}</span></div>;
    $[41] = valueRounded.suffix;
    $[42] = valueRounded.value;
    $[43] = t29;
  } else {
    t29 = $[43];
  }
  let t30;
  if ($[44] !== t24 || $[45] !== t27 || $[46] !== t29) {
    t30 = <div className={t24}>{t27}{t29}</div>;
    $[44] = t24;
    $[45] = t27;
    $[46] = t29;
    $[47] = t30;
  } else {
    t30 = $[47];
  }
  let t31;
  if ($[48] !== doughnutData.name || $[49] !== t11 || $[50] !== t19 || $[51] !== t30 || $[52] !== t5 || $[53] !== t6 || $[54] !== t7) {
    t31 = <div key={t4} onClick={t5} onMouseEnter={t6} onMouseLeave={t7} className={t11}>{t19}{t30}</div>;
    $[48] = doughnutData.name;
    $[49] = t11;
    $[50] = t19;
    $[51] = t30;
    $[52] = t5;
    $[53] = t6;
    $[54] = t7;
    $[55] = t31;
  } else {
    t31 = $[55];
  }
  return t31;
}
export { ItemLegendDoughnut };